import { ref } from 'vue';
import { uploadRefImage, addAsset, imageCompressor, readFileAsBase64 } from '@/lib/asset';
import { getUser } from '@/lib/user';
import { $t } from '@/i18n';

export function useAttachmentUpload() {
  const isUploading = ref({
    reference: false,
    material: false,
    document: false,
  });

  const uploadProgress = ref({
    reference: 0,
    material: 0,
    document: 0,
  });

  function validateFile(file, type, onError) {
    const maxSizes = {
      reference: 15 * 1024 * 1024,
      material: 15 * 1024 * 1024,
      document: 30 * 1024 * 1024,
    };

    const allowedTypes = {
      reference: ['image/png', 'image/jpeg', 'image/webp'],
      material: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
      document: [
        'text/plain',
        'application/pdf',
        'text/markdown',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
    };

    const maxSize = maxSizes[type] || 30 * 1024 * 1024;

    if (file.size > maxSize) {
      onError?.($t('attach.fileTooBig'));
      return false;
    }

    const isValidType = allowedTypes[type].some((allowedType) => {
      if (type === 'document') {
        return (
          file.type === allowedType ||
          file.name.toLowerCase().endsWith('.txt') ||
          file.name.toLowerCase().endsWith('.md') ||
          file.name.toLowerCase().endsWith('.doc') ||
          file.name.toLowerCase().endsWith('.docx')
        );
      }
      return file.type === allowedType;
    });

    if (!isValidType) {
      onError?.($t('attach.unsupportedFormat'));
      return false;
    }

    return true;
  }

  async function uploadReference(file, callbacks = {}) {
    if (!file || !validateFile(file, 'reference', callbacks.onError)) return;

    isUploading.value.reference = true;
    uploadProgress.value.reference = 0;

    try {
      await uploadRefImage(
        file,
        (payload) => {
          callbacks.onSuccess?.(payload);
        },
        (progress) => {
          uploadProgress.value.reference = progress;
        }
      );
    } catch (error) {
      console.error('Failed to upload reference image:', error);
      callbacks.onError?.($t('attach.uploadFailed'));
    } finally {
      isUploading.value.reference = false;
      uploadProgress.value.reference = 0;
    }
  }

  async function uploadMaterial(files, callbacks = {}) {
    const validFiles = Array.from(files).filter((file) =>
      validateFile(file, 'material', callbacks.onError)
    );
    if (validFiles.length === 0) return;

    isUploading.value.material = true;
    uploadProgress.value.material = 0;

    try {
      const user = getUser();
      if (!user) {
        throw new Error('User not authenticated, cannot upload material');
      }
      // Note: Authentication check is assumed to be done by caller or handled globally if user is null

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const fileStartPercent = (i / validFiles.length) * 100;
        const filePercentSpan = 100 / validFiles.length;

        uploadProgress.value.material = fileStartPercent;
        const img = await imageCompressor(file, { maxSize: 1920 });

        // Progress hack
        uploadProgress.value.material = fileStartPercent + filePercentSpan * 0.5;

        const asset = await addAsset(
          img.dataURL,
          { filename: file.name, source: 'material' },
          (progress) => {
            uploadProgress.value.material = fileStartPercent + filePercentSpan * (progress / 100);
          }
        );
        callbacks.onPick?.(asset);

        uploadProgress.value.material = fileStartPercent + filePercentSpan;
      }
      callbacks.onSuccess?.();
    } catch (error) {
      console.error('Failed to upload material:', error);
      callbacks.onError?.($t('attach.uploadFailed'));
    } finally {
      isUploading.value.material = false;
      uploadProgress.value.material = 0;
    }
  }

  async function uploadDocument(files, callbacks = {}) {
    const validFiles = Array.from(files).filter((file) =>
      validateFile(file, 'document', callbacks.onError)
    );
    if (validFiles.length === 0) return;

    isUploading.value.document = true;
    uploadProgress.value.document = 0;

    try {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const fileStartPercent = (i / validFiles.length) * 100;
        const filePercentSpan = 100 / validFiles.length;

        uploadProgress.value.document = fileStartPercent + filePercentSpan * 0.2;
        const text = await readFileAsBase64(file);
        uploadProgress.value.document = fileStartPercent + filePercentSpan * 0.5;

        const asset = await addAsset(
          text,
          { filename: file.name, source: 'document' },
          (progress) => {
            uploadProgress.value.document = fileStartPercent + filePercentSpan * (progress / 100);
          }
        );

        callbacks.onPick?.(asset);
        uploadProgress.value.document = fileStartPercent + filePercentSpan;
      }
      callbacks.onSuccess?.();
    } catch (error) {
      console.error('Failed to upload document:', error);
      callbacks.onError?.($t('attach.uploadFailed'));
    } finally {
      isUploading.value.document = false;
      uploadProgress.value.document = 0;
    }
  }

  return {
    isUploading,
    uploadProgress,
    uploadReference,
    uploadMaterial,
    uploadDocument,
    validateFile,
  };
}
